import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { TestSublease } from '../../types/test-sublease';
import { supabase } from '../../lib/supabase';
import { supabaseLotService } from '../../services/supabase/lots'; // Import lot service
import { format } from 'date-fns';

interface AddTestSubLeaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddTestSubLeaseModal({
  isOpen,
  onClose,
  onSuccess
}: AddTestSubLeaseModalProps) {
  // Valid rental frequency options
  const rentalFrequencyOptions = [
    "Monthly",
    "Quarterly",
    "Annually",
    "When requested"
  ];

  // Valid lease method options with descriptions
  const leaseMethodOptions = [
    { code: null, description: "None" },
    { code: "C", description: "Non-Retail Commercial (Non-Government)" },
    { code: "CG", description: "Non-Retail Commercial (Government)" },
    { code: "CH", description: "Community housing (peppercorn)" },
    { code: "PS", description: "Public Space" },
    { code: "R3", description: "Retail - (Commercial) (Under 3 million)" },
    { code: "R5", description: "Retail - (Commercial) (Gross income over 5 mil)" },
    { code: "RG", description: "Residential (Government)" },
    { code: "RN", description: "Residential (Non-Government)" },
    { code: "SD", description: "Short-term Development Sublease" },
    { code: "U", description: "Undeveloped (large parcels)" }
  ];

  const getInitialFormData = () => ({
    lease_id: '',
    lease_holder: '',
    lease_start: format(new Date(), 'yyyy-MM-dd'),
    lease_end: format(new Date(new Date().setFullYear(new Date().getFullYear() + 1)), 'yyyy-MM-dd'),
    lease_method: null, // Default to null (None)
    lease_type: '',
    lease_furth_term: '',
    lease_rent_freq: rentalFrequencyOptions[0], // Default to first option (Monthly)
    lease_dealing: '',
    lease_deal_doc: ''
  });

  // Define the type for the form data
  interface FormData {
    lease_id: string;
    lease_holder: string;
    lease_start: string;
    lease_end: string;
    lease_method: string | null;
    lease_type: string;
    lease_furth_term: string;
    lease_rent_freq: string;
    lease_dealing: string;
    lease_deal_doc: string;
  }

  const [formData, setFormData] = useState<FormData>(getInitialFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leaseholders, setLeaseholders] = useState<{id: string, name: string}[]>([]);
  // New state variables for lot selection
  const [availableLots, setAvailableLots] = useState<Array<{lot_id: string, lot_desc: string | null, community_name?: string | null}>>([]);
  const [selectedLots, setSelectedLots] = useState<string[]>([]);
  const [leaseLotUse, setLeaseLotUse] = useState<string>('');

  // Reset form state
  const resetForm = () => {
    setFormData(getInitialFormData());
    setSelectedLots([]); // Reset selected lots
    setLeaseLotUse(''); // Reset lease lot use
    setError(null);
    setIsSubmitting(false);
  };

  // Wrap the original onClose to reset form state
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Helper to enhance lot data with community names
  const enhanceLotsWithCommunityNames = async (lotsData: any[]) => {
    const communityIds = lotsData
      .map(lot => lot.lot_community)
      .filter((id): id is string => id !== null && id !== undefined);

    let communityMap: Record<string, string> = {};
    if (communityIds.length > 0) {
      try {
        const { data: communities, error: commError } = await supabase
          .from('communities')
          .select('comm_id, comm_name')
          .in('comm_id', communityIds);
        if (!commError && communities) {
          communityMap = communities.reduce((map, comm) => {
            map[comm.comm_id] = comm.comm_name;
            return map;
          }, {} as Record<string, string>);
        } else if (commError) console.warn('Could not fetch community names:', commError);
      } catch (commFetchError) {
        console.error('Error fetching communities:', commFetchError);
      }
    }
    return lotsData.map(lot => ({
      ...lot, // Spread existing lot properties
      lot_id: lot.lot_id, // Ensure lot_id is present
      lot_desc: lot.lot_desc || 'No description',
      community_name: lot.lot_community ? communityMap[lot.lot_community] || `Community ${lot.lot_community}` : 'Unknown'
    }));
  };

  // Fetch leaseholders and lots for dropdown
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch leaseholders
        const { data: leaseholderData, error: leaseholderError } = await supabase
          .from('leaseholders')
          .select('holder_id, holder_name')
          .order('holder_name', { ascending: true });

        if (leaseholderError) throw leaseholderError;

        setLeaseholders(leaseholderData.map(item => ({
          id: item.holder_id,
          name: item.holder_name
        })));

        // Generate a new lease ID
        await generateLeaseId(); // Ensure ID is generated before fetching lots

        // Fetch all selectable lots (lot_avail = 'Y')
        const selectableLotsData = await supabaseLotService.getSelectableLotsForAssociation();
        const enhancedSelectableLots = await enhanceLotsWithCommunityNames(selectableLotsData);
        setAvailableLots(enhancedSelectableLots);

      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load form data');
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);


  // Generate a new lease ID
  const generateLeaseId = async () => {
    try {
      // Get the highest lease_id from the database
      const { data, error } = await supabase
        .from('subleases')
        .select('lease_id')
        .order('lease_id', { ascending: false })
        .limit(1);

      if (error) throw error;

      let newLeaseId = '1001'; // Default starting ID

      if (data && data.length > 0) {
        // Increment the highest lease_id by 1
        const highestId = parseInt(data[0].lease_id);
        newLeaseId = (highestId + 1).toString();
      }

      setFormData(prev => ({ ...prev, lease_id: newLeaseId }));
    } catch (err) {
      console.error('Error generating lease ID:', err);
      setError('Failed to generate lease ID');
    }
  };

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // Special handling for the lease_type field to ensure consistent formatting
    if (name === 'lease_type') {
      // Store the value as-is, but we'll handle it properly during submission
      setFormData(prev => ({ ...prev, [name]: value }));
      console.log(`Setting lease_type to: "${value}", length: ${value.length}, type: ${typeof value}`);
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Handle checkbox change for lot selection
  const handleLotCheckboxChange = (lotId: string) => {
    setSelectedLots(prev => {
      if (prev.includes(lotId)) {
        return prev.filter(id => id !== lotId);
      } else {
        return [...prev, lotId];
      }
    });
  };

  // Handle leaseLotUse change
  const handleLeaseLotUseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLeaseLotUse(e.target.value);
  };


  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Optional validation for lots
    if (selectedLots.length === 0) {
      setError('Please select at least one lot to associate with this sublease');
      setIsSubmitting(false);
      return;
    }

    // Create a timeout promise to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timed out after 10 seconds')), 10000);
    });


    try {
      console.log('Adding new sublease with ID:', formData.lease_id);

      // Create the insert data object with explicit null handling
      const lease_method = formData.lease_method === '' || formData.lease_method === null ? null : formData.lease_method;
      // Apply similar string handling for lease_type as we do in the edit modal
      const lease_type = formData.lease_type?.trim() || null;

      console.log('Lease type value:', {
        rawValue: formData.lease_type,
        processedValue: lease_type,
        type: typeof formData.lease_type,
        length: formData.lease_type?.length
      });

      const insertData = {
        lease_id: formData.lease_id,
        lease_holder: formData.lease_holder,
        lease_start: formData.lease_start,
        lease_end: formData.lease_end,
        lease_method: lease_method,
        lease_type: lease_type, // Use the properly processed value
        lease_furth_term: formData.lease_furth_term,
        lease_rent_freq: formData.lease_rent_freq,
        lease_dealing: formData.lease_dealing,
        lease_deal_doc: formData.lease_deal_doc
      };

      // Get Supabase connection details
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Missing Supabase environment variables');
      }

      // 1. First create the sublease record
      const subleasePromise = fetch(
        `${supabaseUrl}/rest/v1/subleases`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
            'Prefer': 'return=representation'
          },
          body: JSON.stringify(insertData)
        }
      );

      const subleaseResponse = await Promise.race([subleasePromise, timeoutPromise]) as Response;


      if (!subleaseResponse.ok) {
        const errorText = await subleaseResponse.text();
        throw new Error(`HTTP error creating sublease! status: ${subleaseResponse.status}, message: ${errorText}`);
      }

      const subleaseResult = await subleaseResponse.json();
      console.log('Sublease created successfully:', subleaseResult);

      // 2. Now create lease_lots records for each selected lot
      const leaseLotsPromises = selectedLots.map(lotId => {
        const leaseLotData = {
          leaselot_lease: parseInt(formData.lease_id), // Ensure lease_id is number if required by DB
          leaselot_lot: lotId,
          leaselot_use: leaseLotUse || null // Use null if empty
        };

        const leaseLotPromise = fetch(
          `${supabaseUrl}/rest/v1/lease_lots`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': supabaseKey,
              'Authorization': `Bearer ${supabaseKey}`,
              'Prefer': 'return=representation'
            },
            body: JSON.stringify(leaseLotData)
          }
        );
        return Promise.race([leaseLotPromise, timeoutPromise]) as Promise<Response>;
      });

      // Execute all lease_lots creation requests
      const leaseLotsResponses = await Promise.allSettled(leaseLotsPromises);

      // Check for any failed lease_lots creations
      const failedLeaseLots = leaseLotsResponses.filter(
        response => response.status === 'rejected' || (response.status === 'fulfilled' && !response.value.ok)
      );

      if (failedLeaseLots.length > 0) {
        console.warn(`${failedLeaseLots.length} out of ${selectedLots.length} lease_lots creations failed`);
        // Optionally, collect error messages from failed requests
        const errorMessages = await Promise.all(failedLeaseLots.map(async (failure) => {
            if (failure.status === 'rejected') return failure.reason.message;
            if (failure.status === 'fulfilled') return `HTTP ${failure.value.status}: ${await failure.value.text()}`;
            return 'Unknown error';
        }));
        setError(`Sublease created, but failed to associate ${failedLeaseLots.length} lots. Errors: ${errorMessages.join(', ')}`);
        // Decide if you still want to call onSuccess and onClose or keep the modal open
        // For now, we'll proceed but show the error
      } else {
         console.log(`Successfully created sublease with ${selectedLots.length} associated lots`);
      }


      // Reset form and close modal (even if some lots failed, the sublease was created)
      resetForm();
      onSuccess(); // Notify parent component of success (even partial)
      onClose();

    } catch (err) {
      console.error('Error adding sublease with lots:', err);
      setError(`Failed to add sublease: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsSubmitting(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Add New Sub-Lease</h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Existing form fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Lease ID (read-only) */}
              <div>
                <label htmlFor="lease_id" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease ID
                </label>
                <input
                  type="text"
                  id="lease_id"
                  name="lease_id"
                  value={formData.lease_id}
                  readOnly
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>

              {/* Leaseholder */}
              <div>
                <label htmlFor="lease_holder" className="block text-sm font-medium text-gray-700 mb-1">
                  Leaseholder
                </label>
                <select
                  id="lease_holder"
                  name="lease_holder"
                  value={formData.lease_holder}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  required
                >
                  <option value="">Select a leaseholder</option>
                  {leaseholders.map(leaseholder => (
                    <option key={leaseholder.id} value={leaseholder.id}>
                      {leaseholder.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lease Start */}
              <div>
                <label htmlFor="lease_start" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease Start Date
                </label>
                <input
                  type="date"
                  id="lease_start"
                  name="lease_start"
                  value={formData.lease_start}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  required
                />
              </div>

              {/* Lease End */}
              <div>
                <label htmlFor="lease_end" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease End Date
                </label>
                <input
                  type="date"
                  id="lease_end"
                  name="lease_end"
                  value={formData.lease_end}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  required
                />
              </div>

              {/* Lease Method */}
              <div>
                <label htmlFor="lease_method" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease Method
                </label>
                <select
                  id="lease_method"
                  name="lease_method"
                  value={formData.lease_method === null ? '' : formData.lease_method}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : e.target.value;
                    setFormData(prev => ({ ...prev, lease_method: value }));
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                >
                  {leaseMethodOptions.map(option => (
                    <option key={option.code || 'none'} value={option.code || ''}>
                      {option.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lease Type */}
              <div>
                <label htmlFor="lease_type" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease Type
                </label>
                <input
                  type="text"
                  id="lease_type"
                  name="lease_type"
                  value={formData.lease_type || ''}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>

              {/* Further Term */}
              <div>
                <label htmlFor="lease_furth_term" className="block text-sm font-medium text-gray-700 mb-1">
                  Further Term
                </label>
                <input
                  type="text"
                  id="lease_furth_term"
                  name="lease_furth_term"
                  value={formData.lease_furth_term || ''}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>

              {/* Rental Frequency */}
              <div>
                <label htmlFor="lease_rent_freq" className="block text-sm font-medium text-gray-700 mb-1">
                  Rental Frequency
                </label>
                <select
                  id="lease_rent_freq"
                  name="lease_rent_freq"
                  value={formData.lease_rent_freq}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  required
                >
                  {rentalFrequencyOptions.map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lease Dealing No. */}
              <div>
                <label htmlFor="lease_dealing" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease Dealing No.
                </label>
                <input
                  type="text"
                  id="lease_dealing"
                  name="lease_dealing"
                  value={formData.lease_dealing || ''}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                />
              </div>
            </div>

            {/* Lease Docs */}
            <div>
              <label htmlFor="lease_deal_doc" className="block text-sm font-medium text-gray-700 mb-1">
                Lease Documents
              </label>
              <textarea
                id="lease_deal_doc"
                name="lease_deal_doc"
                value={formData.lease_deal_doc || ''}
                onChange={handleChange}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
              />
            </div>

            {/* Lot Selection Section */}
            <div className="mt-8 border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Associate Lots</h3>

              {/* Lease Lot Use field */}
              <div className="mb-4">
                <label htmlFor="leaseLotUse" className="block text-sm font-medium text-gray-700 mb-1">
                  Lease Lot Use
                </label>
                <input
                  type="text"
                  id="leaseLotUse"
                  name="leaseLotUse"
                  value={leaseLotUse}
                  onChange={handleLeaseLotUseChange}
                  placeholder="Specify the permitted use for selected lots"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  maxLength={200} // As per image annotation
                />
                <p className="mt-1 text-sm text-gray-500">
                  This use will be applied to all selected lots. Max 200 characters.
                </p>
              </div>

              {/* Lot Selection Logic */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Available Lots to Associate ({selectedLots.length} selected)
                </label>
                {availableLots.length === 0 ? (
                  <div className="text-sm text-gray-500 italic p-4 border border-gray-200 rounded-md">
                    Loading available lots or none found...
                  </div>
                ) : (
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-2">
                    {availableLots.map(lot => (
                      <div key={`available-${lot.lot_id}`} className="flex items-start p-2 hover:bg-gray-50 rounded">
                        <input
                          type="checkbox"
                          id={`available-lot-${lot.lot_id}`}
                          checked={selectedLots.includes(lot.lot_id)}
                          onChange={() => handleLotCheckboxChange(lot.lot_id)}
                          className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                        />
                        <label htmlFor={`available-lot-${lot.lot_id}`} className="ml-2 block text-sm cursor-pointer">
                          <span className="font-medium">{lot.lot_id}</span>
                          {lot.community_name && <span className="text-gray-500 ml-2">- {lot.community_name}</span>}
                          {lot.lot_desc && lot.lot_desc !== 'No description' && (
                            <p className="text-xs text-gray-500 mt-0.5">{lot.lot_desc}</p>
                          )}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                {selectedLots.length > 0 && (
                  <p className="mt-4 text-sm text-gray-600 font-semibold">
                    Total: {selectedLots.length} {selectedLots.length === 1 ? 'lot' : 'lots'} selected
                  </p>
                )}
              </div>
            </div>


            {/* Submission Buttons */}
            <div className="flex justify-end space-x-3 mt-8 border-t pt-6">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
              >
                {isSubmitting ? 'Adding...' : 'Add Sub-Lease & Associate Lots'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
